/**
 * 超清放大模块注册表
 *
 * 使用注册表模式管理重绘放大和快速放大两种实现，
 * 避免在调用方写条件分支链。
 *
 * @see research.md 第2节 - 模块注册表模式
 */

import type { ZoomMode, ZoomParams } from '@/types/artwork/zoom';

// ============================================================
// 模块接口定义
// ============================================================

/** Zoom 模块接口定义 */
export interface ZoomModule {
  /** 启动生成 */
  startGenerate: (params: ZoomParams) => Promise<void>;
  /** 计算算力消耗 */
  getConsumePower: (params?: any) => number;
  /** 计算预计时间 */
  getConsumeTime: (params?: any) => number;
}

/** 模块加载器类型 */
type ZoomModuleLoader = () => Promise<ZoomModule>;

/** 注册表 */
const registry: Partial<Record<ZoomMode, ZoomModuleLoader>> = {};

/**
 * 注册放大模式模块
 * @param mode 放大模式
 * @param loader 模块加载器（支持动态导入）
 */
export function registerZoomModule(mode: ZoomMode, loader: ZoomModuleLoader): void {
  registry[mode] = loader;
}

/**
 * 解析放大模式模块
 * @param mode 放大模式
 * @returns Zoom 模块实例
 */
export async function resolveZoomModule(mode: ZoomMode): Promise<ZoomModule> {
  const loader = registry[mode];
  if (!loader) {
    throw new Error(`[zoomRegistry] Unknown zoom mode: ${mode}`);
  }
  return loader();
}

/**
 * 检查放大模式是否已注册
 * @param mode 放大模式
 */
export function isZoomModeRegistered(mode: ZoomMode): boolean {
  return mode in registry;
}

/**
 * 获取所有已注册的放大模式
 */
export function getRegisteredZoomModes(): ZoomMode[] {
  return Object.keys(registry) as ZoomMode[];
}

// ============================================================
// 便捷函数：直接调用模块方法
// ============================================================

/**
 * 启动放大生成
 * @param mode 放大模式
 * @param params 生成参数
 */
export async function startZoomGenerateByMode(mode: ZoomMode, params: ZoomParams): Promise<void> {
  const module = await resolveZoomModule(mode);
  return module.startGenerate(params);
}

/**
 * 获取算力消耗
 * @param mode 放大模式
 * @param params 参数
 */
export async function getZoomConsumePowerByMode(
  mode: ZoomMode,
  params: Partial<ZoomParams>
): Promise<number> {
  const module = await resolveZoomModule(mode);
  return module.getConsumePower(params);
}

/**
 * 获取预计时间
 * @param mode 放大模式
 * @param params 参数
 */
export async function getZoomConsumeTimeByMode(
  mode: ZoomMode,
  params: Partial<ZoomParams>
): Promise<number> {
  const module = await resolveZoomModule(mode);
  return module.getConsumeTime(params);
}

// ============================================================
// 编辑模式解析
// ============================================================

import { ModuleId } from '@xmiles/holopix-types';

/**
 * 根据模块 ID 解析编辑模式
 * @param moduleId 模块 ID
 * @returns 放大模式
 */
export function resolveEditModeByModuleId(moduleId: number): ZoomMode {
  switch (moduleId) {
    case ModuleId.Zoom: // 20
      return 'fast';
    case ModuleId.RepaintUpscale: // 59
      return 'redraw';
    default:
      console.warn(`[zoomRegistry] Unknown module ID: ${moduleId}, fallback to fast`);
      return 'fast';
  }
}
